import type { StatusMeaning } from '../status';

/** Used only by the Director lifecycle, not scheduling or scoring. */
export const DIRECTOR_LIFECYCLE_SESSION_DURATION_MS = 4 * 60 * 60 * 1000;

export const DIRECTOR_LIFECYCLE = {
  rostering: { label: 'Rostering', tone: 'neutral' },
  ready: { label: 'Ready', tone: 'neutral' },
  in_play: { label: 'In Play', tone: 'positive' },
  finished: { label: 'Finished', tone: 'neutral' },
  completed: { label: 'Completed', tone: 'neutral' },
} as const satisfies Record<string, StatusMeaning>;

export interface DirectorLifecycleInput {
  status: string;
  sessions: readonly { startsAt: string | Date; status: string; hasSavedSeating?: boolean }[];
}

export interface DirectorLifecycleResult {
  state: keyof typeof DIRECTOR_LIFECYCLE | null;
  nextChangeAt: number | null;
  exception: 'all_sessions_voided' | null;
}

/**
 * A LEAGUE THAT IS OVER, and the two statuses that mean it.
 *
 * `completed` is written by close-out and `archived` by the director; both mean the same thing to
 * everything downstream — no more sessions, no more scores, nothing left to run. The pair was
 * spelled out by hand in half a dozen places across the API and this module, which is fine until
 * one of them gains a third status and the others do not.
 *
 * It earns a name now because billing reads it: an unfinished league counts against what a
 * membership includes, and a finished one is free forever. A director in her third year is charged
 * for what she is running, never for her history — so which side of this line a league falls on is
 * the difference between a charge and no charge.
 */
export const FINISHED_LEAGUE_STATUSES = ['completed', 'archived'] as const;

export function isFinishedLeague(status: string): boolean {
  return (FINISHED_LEAGUE_STATUSES as readonly string[]).includes(status);
}

export function directorLifecycle(
  input: DirectorLifecycleInput,
  now: number,
): DirectorLifecycleResult {
  const result = (
    state: DirectorLifecycleResult['state'],
    nextChangeAt: number | null = null,
  ): DirectorLifecycleResult => ({ state, nextChangeAt, exception: null });

  if (isFinishedLeague(input.status)) {
    return result('completed');
  }
  if (input.sessions.length === 0) return result('rostering');

  const scheduled = input.sessions.filter((session) => session.status !== 'voided');
  if (scheduled.length === 0) {
    return { state: null, nextChangeAt: null, exception: 'all_sessions_voided' };
  }

  const starts = scheduled.map((session) => new Date(session.startsAt).getTime());
  const firstStart = Math.min(...starts);
  const finalEnd = Math.max(...starts) + DIRECTOR_LIFECYCLE_SESSION_DURATION_MS;

  if (now < firstStart) {
    const first = scheduled.find((session) => new Date(session.startsAt).getTime() === firstStart)!;
    return result(first.hasSavedSeating ? 'ready' : 'rostering', firstStart);
  }
  if (now < finalEnd) return result('in_play', finalEnd);
  return result('finished');
}

/** Timing only; score review and acknowledgments remain server safeguards. */
export function directorCloseOutTiming(input: DirectorLifecycleInput, now: number) {
  if (isFinishedLeague(input.status)) {
    return { eligible: false, availableAt: null, reason: 'already_closed' } as const;
  }
  const scheduled = input.sessions.filter((session) => session.status !== 'voided');
  if (scheduled.length === 0) {
    return {
      eligible: false,
      availableAt: null,
      reason: input.sessions.length ? 'all_sessions_voided' : 'no_sessions',
    } as const;
  }
  const availableAt = Math.max(...scheduled.map((session) => new Date(session.startsAt).getTime()));
  return { eligible: now >= availableAt, availableAt, reason: now >= availableAt ? null : 'final_session_not_started' } as const;
}
